import type { WorkflowScriptListItem } from '@/types/workflow';
import {
  recommendationBandKey,
  scriptRecommendationLabel,
  type RecommendationBandKey,
  type RecommendationLabel,
} from '@/config/recommendation';

export type SlateBand = RecommendationBandKey | 'queued' | 'active';

export type SlateCell = {
  format: string;
  band: SlateBand;
  count: number;
};

const RECOMMENDATION_BANDS: RecommendationBandKey[] = [
  'greenlight',
  'develop',
  'consider',
  'hold',
  'pass',
];

const PIPELINE_BANDS: SlateBand[] = ['queued', 'active'];

const BANDS: SlateBand[] = [...RECOMMENDATION_BANDS, ...PIPELINE_BANDS];

const BAND_LABELS: Record<SlateBand, string> = {
  greenlight: 'Greenlight',
  develop: 'Develop',
  consider: 'Consider',
  hold: 'Hold',
  pass: 'Pass',
  queued: 'Queued',
  active: 'Active',
};

export function bandLabel(band: SlateBand): string {
  return BAND_LABELS[band];
}

function scriptBand(script: WorkflowScriptListItem): SlateBand {
  const steps = script.steps;
  if (steps.analyse === 'in_progress') return 'active';
  if (steps.analyse !== 'complete' && steps.upload === 'complete') return 'active';
  if (steps.analyse !== 'complete') return 'queued';
  const label = scriptRecommendationLabel(script);
  if (label) return recommendationBandKey(label);
  return 'queued';
}

/** Infer format bucket from script title or default. */
function inferFormat(script: WorkflowScriptListItem): string {
  const title = (script.script_title ?? '').toLowerCase();
  if (title.includes('pilot') || title.includes('series')) return 'Series';
  if (title.includes('feature') || title.includes('film')) return 'Feature';
  if (title.includes('limited')) return 'Limited';
  return 'Screenplay';
}

export function buildSlateHeatmap(scripts: WorkflowScriptListItem[]): {
  formats: string[];
  bands: SlateBand[];
  matrix: Record<string, Record<SlateBand, number>>;
  max: number;
} {
  const matrix: Record<string, Record<SlateBand, number>> = {};
  const formatSet = new Set<string>();

  for (const script of scripts) {
    const format = inferFormat(script);
    const band = scriptBand(script);
    formatSet.add(format);
    if (!matrix[format]) {
      matrix[format] = Object.fromEntries(BANDS.map((b) => [b, 0])) as Record<SlateBand, number>;
    }
    matrix[format][band] += 1;
  }

  const formats = formatSet.size > 0 ? Array.from(formatSet).sort() : ['Screenplay'];
  if (!matrix['Screenplay'] && formats.length === 1 && formats[0] === 'Screenplay') {
    matrix['Screenplay'] = Object.fromEntries(BANDS.map((b) => [b, 0])) as Record<SlateBand, number>;
  }

  let max = 0;
  for (const format of formats) {
    for (const band of BANDS) {
      max = Math.max(max, matrix[format]?.[band] ?? 0);
    }
  }

  return { formats, bands: BANDS, matrix, max };
}

export function recommendationBandFromLabel(label: RecommendationLabel): RecommendationBandKey {
  return recommendationBandKey(label);
}
